<template>
  <PageLayout>
    <template v-slot:header>
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
        <button
          v-if="!$can('access', 'read-only')"
          id="btn-invite-page"
          type="button"
          @click="inviteUsers"
          class="btn btn-tertiary btn-lg mr-3"
        >
          <i class="material-icons">
            person_add
          </i>
          <span class="d-none d-lg-inline-block ml-2">
            {{ resources.general.buttons.invite }}
          </span>
        </button>
        <div class="float-right d-flex">
          <b-button
            id="users-filter-btn"
            variant="icon"
            :class="{ selected: hasFilter }"
            style="display: none;"
            @click="toggleFilterPopover = !toggleFilterPopover"
            v-if="hasFilter || usersSize > 0"
          >
            <i class="material-icons material-icons-no-line-height">
              tune
            </i>
          </b-button>
          <Popover
            :toggle="toggleFilterPopover"
            class="popover-position user-popover"
            :small="true"
          >
            <b-col cols="12 mb-4">
              <b-form-group
                :label="labels.userResultLabel.filter.name"
                label-size="sm"
              >
                <b-input-group>
                  <b-form-input
                    autocomplete="off"
                    id="filter-by-name"
                    v-model="filterText"
                    class="input-gray name-form-group"
                    v-on:keyup.13="applyFilters"
                  />
                  <template v-slot:append>
                    <b-button
                      class="apply-form-group-btn"
                      id="filter-apply"
                      variant="primary"
                      size="lg"
                      @click="applyFilters"
                    >
                      <i class="material-icons">search</i>
                    </b-button>
                  </template>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col cols="12 mb-4">
              <b-form-group
                :label="labels.userResultLabel.filter.status"
                label-size="sm"
              >
                <b-form-select
                  id="filter-by-status"
                  v-model="actualStatus"
                  class="input-white"
                  size="lg"
                  @change="applyFilters"
                >
                  <option value="undefined">{{ labels.select }}</option>
                  <option value="unconfirmed">
                    {{ labels.type.unconfirmed }}
                  </option>
                  <option value="confirmed">
                    {{ labels.type.confirmed }}
                  </option>
                  <option value="inactivated">
                    {{ labels.type.inactivated }}
                  </option>
                  <option value="deactivated">
                    {{ labels.type.deactivated }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="12 mb-4">
              <b-form-group
                :label="labels.userResultLabel.filter.type"
                label-size="sm"
              >
                <b-form-select
                  id="filter-by-role"
                  v-model="filterTypeRole"
                  class="input-white"
                  size="lg"
                  @change="applyFilters"
                >
                  <option value="null">{{ labels.select }}</option>
                  <option value="admin">{{ labels.roles.admin }}</option>
                  <option value="manager">{{ labels.roles.manager }}</option>
                  <option value="user">{{ labels.roles.user }}</option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <template v-slot:footer>
              <b-button
                id="filter-clear"
                variant="link"
                class="m-3 float-left"
                size="sm"
                @click="clearAndCloseFilters"
                >{{ resources.general.buttons.clear }}</b-button
              >
            </template>
          </Popover>
        </div>
        <ExportData
          v-if="!$can('access', 'read-only')"
          id="exportData"
          class="ml-sm-2 mt-2 mt-sm-0"
          :canExport="$can('use', 'users.export')"
          @export="handleExportClick"
        />
      </div>
      <MainNavbar />
    </template>
    <template v-slot:main>
      <b-modal
        v-if="$can('action', 'users.disconfirm')"
        id="confirmDisconfirmation"
        ref="confirmDisconfirmation"
        :title="labels.modal.title"
        :ok-title="resources.general.buttons.yes"
        ok-variant="primary"
        :cancel-title="resources.general.buttons.no"
        @ok="doDisconfirmation"
        cancel-variant="white"
        button-size="large"
      >
        <p class="my-2">
          {{ labels.modal.confirmDisconfirmationMessage }}
        </p>
      </b-modal>

      <b-modal
        id="confirmDelete"
        ref="confirmDelete"
        :title="labels.modal.title"
        :ok-title="resources.general.buttons.yes"
        ok-variant="primary"
        :cancel-title="resources.general.buttons.no"
        @ok="doDelete"
        cancel-variant="white"
        button-size="large"
      >
        <p class="my-2 pb-3">
          {{ labels.modal.deleteConfirmMessage }}
        </p>
        <b-alert show class="modal-alert">
          <p>{{ labels.modal.deleteConfirmText1 }}</p>
          <p>{{ labels.modal.deleteConfirmText2 }}</p>
          <ul>
            <li>{{ labels.modal.deleteConfirmItem1 }}</li>
            <li>{{ labels.modal.deleteConfirmItem2 }}</li>
            <li>{{ labels.modal.deleteConfirmItem3 }}</li>
          </ul>
        </b-alert>
      </b-modal>

      <b-modal
        id="confirmInactivate"
        ref="confirmInactivate"
        :title="labels.modal.title"
        :ok-title="resources.general.buttons.yes"
        ok-variant="primary"
        :cancel-title="resources.general.buttons.no"
        @ok="doInactivate"
        cancel-variant="white"
        button-size="large"
      >
        <p class="my-2 pb-3">
          {{ labels.modal.inactivateConfirmMessage }}
        </p>
        <b-alert show class="modal-alert">
          <p>{{ labels.modal.inactivateConfirmText1 }}</p>
          <p>{{ labels.modal.inactivateConfirmText2 }}</p>
          <ul>
            <li>{{ labels.modal.inactivateConfirmItem1 }}</li>
            <li>{{ labels.modal.inactivateConfirmItem2 }}</li>
            <li>{{ labels.modal.inactivateConfirmItem3 }}</li>
          </ul>
        </b-alert>
      </b-modal>

      <b-modal
        id="confirmCancelInvitation"
        ref="confirmCancelInvitation"
        :title="labels.modal.title"
        :ok-title="resources.general.buttons.yes"
        ok-variant="primary"
        :cancel-title="resources.general.buttons.no"
        @ok="doCancelInvitation"
        cancel-variant="white"
        button-size="large"
      >
        <p class="my-2">
          {{ labels.modal.cancelInvitationConfirmMessage }}
        </p>
      </b-modal>

      <b-modal
        ref="confirmAnonymizeUser"
        :title="labels.modal.title"
        :ok-title="resources.general.buttons.yes"
        ok-variant="primary"
        :cancel-title="resources.general.buttons.no"
        @ok="doAnonymizeUser"
        cancel-variant="white"
        button-size="large"
      >
        <p class="my-2 pb-3">
          {{ labels.modal.confirmAnonymizeUserMessage }}
        </p>
        <b-alert show class="modal-secondary">
          <div class="d-flex">
            <i class="material-icons">warning</i>
            <span>{{ labels.modal.confirmAnonymizeUserAlert }}</span>
          </div>
        </b-alert>
      </b-modal>

      <modal-exceed-license ref="exceedLicense"></modal-exceed-license>

      <modal-plans
        v-if="!$can('use', 'users.export')"
        ref="component-modal-plans"
        :title="labels.modalPlans.titleCSV"
      />

      <b-card class="without-padding-bottom">
        <header slot="header">
          <b-row>
            <b-col lg="12">
              <b-row class="">
                <b-col lg="12">
                  {{ labels.title }}
                </b-col>
                <b-col lg="9" class="text-gray03 pl-2 pt-4">
                  <div v-if="users && users.length">
                    <h6 v-show="usersSize > 0" id="info-about-users-find">
                      {{ usersSize }}
                      {{ resourcesUserResult[actualStatus] }}
                      <span class="company-name">{{ user.companyName }}</span>
                    </h6>
                  </div>
                </b-col>
                <b-col lg="3">
                  <licenses-summary ref="licenseSummary"></licenses-summary>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row class="pb-3 fix-font-size search-filters">
            <b-col lg="6" class="pt-3">
              <b-form-group
                :label="labels.userResultLabel.filter.name"
                label-size="sm"
              >
                <b-input-group>
                  <b-form-input
                    autocomplete="off"
                    id="filter-by-name"
                    v-model="filterText"
                    class="input-gray name-form-group"
                    v-on:keyup.13="applyFilters"
                  />
                  <template v-slot:append>
                    <b-button
                      class="apply-form-group-btn"
                      id="filter-apply"
                      variant="primary"
                      size="lg"
                      @click="applyFilters"
                    >
                      <i class="material-icons">search</i>
                    </b-button>
                  </template>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col lg="3" class="pt-3">
              <b-form-group
                :label="labels.userResultLabel.filter.status"
                label-size="sm"
              >
                <b-form-select
                  id="filter-by-status"
                  v-model="actualStatus"
                  class="input-white"
                  size="lg"
                  @change="applyFilters"
                >
                  <option value="undefined">{{ labels.select }}</option>
                  <option value="unconfirmed">
                    {{ labels.type.unconfirmed }}
                  </option>
                  <option value="confirmed">
                    {{ labels.type.confirmed }}
                  </option>
                  <option value="inactivated">
                    {{ labels.type.inactivated }}
                  </option>
                  <option value="deactivated">
                    {{ labels.type.deactivated }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col lg="3" class="pt-3">
              <b-form-group
                :label="labels.userResultLabel.filter.type"
                label-size="sm"
              >
                <b-form-select
                  id="filter-by-role"
                  v-model="filterTypeRole"
                  class="input-white"
                  size="lg"
                  @change="applyFilters"
                >
                  <option value="null">{{ labels.select }}</option>
                  <option value="admin">{{ labels.roles.admin }}</option>
                  <option value="manager">{{ labels.roles.manager }}</option>
                  <option value="user">{{ labels.roles.user }}</option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </header>

        <div v-if="users === undefined" class="loading">
          <loading />
          <p>{{ labels.loading }}</p>
        </div>

        <div class="empty-state-box" v-show="users && users.length === 0">
          <video muted loop autoplay src="home/empty-state-users.mp4" />
          <label id="doesnt-exist-tickets-box">{{
            labels.noUsersFindMessage
          }}</label>
        </div>

        <div class="full-table-wraper" v-if="users && users.length">
          <b-table
            responsive
            striped
            :fields="fields"
            :items="users"
            class="table-responsive"
          >
            <template v-slot:cell(profile)="data">
              <b-row
                :class="{
                  'padding-row': true,
                  'label-inactive':
                    data.item.inactive && data.item.status !== 'deactivated',
                  'label-deactivated': data.item.status === 'deactivated',
                }"
              >
                <b-col
                  v-if="data.item.status !== 'unconfirmed'"
                  cols="auto"
                  :class="{
                    'text-center': true,
                    'pr-0': true,
                    'pl-0': true,
                    'label-inactive':
                      data.item.inactive && data.item.status !== 'deactivated',
                    'label-deactivated': data.item.status === 'deactivated',
                  }"
                >
                  <i class="material-icons icon-profile">account_circle</i>
                </b-col>
                <b-col class="float-right ellipsis-text p-2">
                  <b-row class="table-font-size">
                    <b-col lg="12" class="ellipsis-text user-status">
                      <span class="medium fullname"
                        >{{ data.item.profile.firstname }}
                        {{ data.item.profile.lastname }}</span
                      >
                      <span>
                        -
                        <span
                          v-if="
                            data.item.inactive &&
                              data.item.status !== 'deactivated'
                          "
                          class="status inactive"
                        >
                          <small>{{ labels.type.inactivated }}</small>
                        </span>
                        <span
                          v-else-if="
                            data.item.status === 'unconfirmed' &&
                              !data.item.inactive
                          "
                          class="status invited"
                        >
                          <small>{{ labels.type.unconfirmed }}</small>
                        </span>
                        <span
                          v-else-if="data.item.status === 'deactivated'"
                          class="status deactivated"
                        >
                          <small>{{ labels.type.deactivated }}</small>
                        </span>
                        <span
                          v-else-if="
                            data.item.status === 'confirmed' &&
                              !data.item.inactive
                          "
                          class="status confirmed"
                        >
                          <small>{{ labels.type.confirmed }}</small>
                        </span>
                      </span>
                    </b-col>
                    <b-col lg="12" class="ellipsis-text">
                      <small>{{ removeFakeEmail(data.item.email) }}</small>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </template>

            <template v-slot:cell(role)="data">
              <div
                :class="{
                  flexbox: true,
                  'label-inactive':
                    data.item.inactive && data.item.status !== 'deactivated',
                  'label-deactivated': data.item.status === 'deactivated',
                }"
              >
                <div class="p-2 table-font-size">
                  <span class="medium">
                    {{ labels.roles[data.item.role] }}
                  </span>
                  <br />
                  <small>{{ user.companyName }}</small>
                </div>
              </div>
            </template>

            <template v-slot:cell(actions)="data">
              <div class="flexbox user-actions">
                <div
                  v-if="
                    $can('action', 'users.disconfirm') &&
                      data.item.status === 'confirmed' &&
                      !data.item.inactive &&
                      isNotMyUser(data.item.id)
                  "
                >
                  <i
                    :id="'confirmed-user-' + data.item.id"
                    @click="confirmDisconfirmation(data.item.id)"
                    class="material-icons text-orange"
                  >
                    replay
                  </i>
                  <b-tooltip :target="'confirmed-user-' + data.item.id">
                    <div class="tooltip-info">
                      {{ resources.general.buttons.disconfirm }}
                    </div>
                  </b-tooltip>
                </div>
                <div
                  v-if="
                    data.item.status !== 'deactivated' &&
                      isNotMyUser(data.item.id)
                  "
                >
                  <i
                    :id="'edit-user-' + data.item.id"
                    @click="edit(data.item)"
                    class="material-icons text-primary"
                  >
                    create
                  </i>
                  <b-tooltip :target="'edit-user-' + data.item.id">
                    <div class="tooltip-info">
                      {{ resources.general.buttons.edit }}
                    </div>
                  </b-tooltip>
                </div>
                <div
                  v-if="
                    data.item.status === 'unconfirmed' && !data.item.inactive
                  "
                >
                  <i
                    :id="'resend-invitation-' + data.item.id"
                    class="material-icons text-warning"
                    @click="resendInvite(data.item.email)"
                  >
                    email
                  </i>
                  <b-tooltip :target="'resend-invitation-' + data.item.id">
                    <div class="tooltip-info">
                      {{ resources.general.buttons.resend }}
                    </div>
                  </b-tooltip>
                </div>
                <div
                  v-if="
                    !$can('access', 'read-only') &&
                      data.item.status !== 'deactivated' &&
                      isNotMyUser(data.item.id)
                  "
                >
                  <i
                    :id="'inactivated-user-' + data.item.id"
                    @click="
                      data.item.inactive
                        ? undoInactivate(data.item.id)
                        : confirmInactivate(data.item.id)
                    "
                    :class="{
                      'material-icons': true,
                      'text-secondary': !data.item.inactive,
                      'text-success': data.item.inactive,
                    }"
                  >
                    power_settings_new
                  </i>
                  <b-tooltip :target="'inactivated-user-' + data.item.id">
                    <div class="tooltip-info">
                      {{
                        data.item.inactive ? labels.activate : labels.inactivate
                      }}
                    </div>
                  </b-tooltip>
                </div>
                <div
                  v-if="
                    !$can('access', 'read-only') &&
                      data.item.status !== 'unconfirmed' &&
                      data.item.status !== 'deactivated' &&
                      isNotMyUser(data.item.id)
                  "
                >
                  <i
                    :id="'delete-user-' + data.item.id"
                    class="material-icons text-gray03"
                    @click="confirmDelete(data.item.id)"
                  >
                    delete
                  </i>
                  <b-tooltip :target="'delete-user-' + data.item.id">
                    <div class="tooltip-info">
                      {{ resources.general.buttons.delete }}
                    </div>
                  </b-tooltip>
                </div>
                <div
                  v-if="
                    !$can('access', 'read-only') &&
                      data.item.status === 'unconfirmed'
                  "
                >
                  <i
                    :id="'cancel-invitation-' + data.item.id"
                    class="material-icons text-danger"
                    @click="confirmCancelInvitation(data.item.id)"
                  >
                    cancel
                  </i>
                  <b-tooltip :target="'cancel-invitation-' + data.item.id">
                    <div class="tooltip-info">
                      {{ resources.general.buttons.cancel }}
                    </div>
                  </b-tooltip>
                </div>
                <div
                  v-if="
                    !$can('access', 'read-only') &&
                      data.item.status === 'deactivated'
                  "
                >
                  <i
                    :id="'reactivate-' + data.item.id"
                    class="material-icons text-primary"
                    @click="reactivate(data.item.id)"
                  >
                    undo
                  </i>
                  <b-tooltip :target="'reactivate-' + data.item.id">
                    <div class="tooltip-info">
                      {{ resources.general.buttons.reactivate }}
                    </div>
                  </b-tooltip>
                </div>
                <div
                  v-if="
                    !$can('access', 'read-only') &&
                      data.item.profile.firstname !== 'anonymous' &&
                      isNotMyUser(data.item.id)
                  "
                  :id="'anonymize-user-' + data.item.id"
                  class="clickable"
                  @click="confirmAnonymizeUser(data.item.id)"
                >
                  <svg style="width:24px;height:24px" viewBox="0 0 24 29">
                    <path
                      fill="currentColor"
                      d="M17.06 13C15.2 13 13.64 14.33 13.24 16.1C12.29 15.69 11.42 15.8 10.76 16.09C10.35 14.31 8.79 13 6.94 13C4.77 13 3 14.79 3 17C3 19.21 4.77 21 6.94 21C9 21 10.68 19.38 10.84 17.32C11.18 17.08 12.07 16.63 13.16 17.34C13.34 19.39 15 21 17.06 21C19.23 21 21 19.21 21 17C21 14.79 19.23 13 17.06 13M6.94 19.86C5.38 19.86 4.13 18.58 4.13 17S5.39 14.14 6.94 14.14C8.5 14.14 9.75 15.42 9.75 17S8.5 19.86 6.94 19.86M17.06 19.86C15.5 19.86 14.25 18.58 14.25 17S15.5 14.14 17.06 14.14C18.62 14.14 19.88 15.42 19.88 17S18.61 19.86 17.06 19.86M22 10.5H2V12H22V10.5M15.53 2.63C15.31 2.14 14.75 1.88 14.22 2.05L12 2.79L9.77 2.05L9.72 2.04C9.19 1.89 8.63 2.17 8.43 2.68L6 9H18L15.56 2.68L15.53 2.63Z"
                    />
                  </svg>
                </div>
                <b-tooltip :target="'anonymize-user-' + data.item.id">
                  <div class="tooltip-info">
                    {{ resources.general.buttons.anonymize }}
                  </div>
                </b-tooltip>
              </div>
            </template>
          </b-table>
        </div>
      </b-card>
      <div class="users-pagination overflow-auto">
        <div class="mt-3" v-if="users && users.length">
          <b-pagination-nav
            :link-gen="linkGen"
            :number-of-pages="numberOfPages"
            align="center"
            use-router
            v-model="page"
          >
          </b-pagination-nav>
        </div>
      </div>
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import ModalExceedLicense from '@/components/Modal/ModalExceedLicense.vue'
import PageLayout from '@/components/Home/PageLayout.vue'
import Popover from '@/components/Shared/Popover.vue'
import LicensesSummary from '@/components/Shared/LicensesSummary.vue'
import { getUser } from '@/service/user'
import { HIGH } from '@/helpers/messages-priority'
import { removeFakeEmail } from '@/helpers/mailFake'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import ExportData from '@/components/Reports/ExportData.vue'
import Loading from '@/components/Shared/Loading.vue'
import MainNavbar from '@/components/Management/MainNavbar'

export default {
  components: {
    Breadcrumb,
    LicensesSummary,
    ModalExceedLicense,
    PageLayout,
    Popover,
    ExportData,
    ModalPlans: () => import('@/components/Modal/ModalPlans.vue'),
    Loading,
    MainNavbar,
  },
  mounted() {
    this.$root.$emit('setPageTitle', { context: this, path: 'users' })
  },
  watch: {
    page: function() {
      const status =
        this.actualStatus === 'undefined' ? undefined : this.actualStatus

      let filters = { status }

      filters.name = this.filterText

      if (this.filterTypeRole !== 'null') {
        filters.role = this.filterTypeRole
      }

      this.offset = (this.page - 1) * this.limit
      filters.offset = this.offset

      this.$store.dispatch('users/fetch', filters)
    },
  },

  computed: {
    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.settings,
        },
      ]
    },
    fields: function() {
      let fields = []
      fields.push({
        key: 'profile',
        label: this.labels.tableHeader.name,
      })
      fields.push({
        key: 'role',
        label: this.labels.tableHeader.role,
      })
      fields.push({
        key: 'actions',
        label: this.labels.tableHeader.actions,
      })
      return fields
    },
    ...mapGetters({
      resources: 'resources',
      users: 'users/users',
      limit: 'users/limit',
      usersSize: 'users/usersSize',
      licenses: 'company/licenses',
    }),
    labels() {
      return this.resources.page.home.users
    },
    resourcesUserResult() {
      return this.labels.userResultLabel
    },
    user() {
      return getUser()
    },
    numberOfPages() {
      const numberOfPages = this.usersSize / this.limit
      if (Number.isInteger(numberOfPages)) {
        return numberOfPages
      }
      return parseInt(numberOfPages) + 1
    },
  },

  data() {
    return {
      actualStatus: undefined, // ['unconfirmed', 'confirmed', 'active', 'inactive', 'deactivated', 'activated', 'inactivated']
      selectedUser: undefined,
      hasFilter: false,
      filterText: undefined,
      filterTypeRole: 'null',
      toggleFilterPopover: false,
      offset: 0,
      page: 1,
      active: false,
    }
  },
  created() {
    const status =
      this.actualStatus === 'undefined' ? undefined : this.actualStatus
    this.$store.dispatch('company/loadLicenses')
    this.$store.dispatch('users/fetch', { status })
    this.clearFilters()
  },

  methods: {
    confirmAnonymizeUser(id) {
      this.selectedUser = id
      this.$refs.confirmAnonymizeUser.show()
    },
    doAnonymizeUser() {
      let doAnonymizeUser = this.$store.dispatch(
        'users/anonymize',
        this.selectedUser
      )
      doAnonymizeUser
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.general.message.anonymizeSuccessfully,
            status: 'success',
          })
          this.$refs.licenseSummary.refreshLicenses()
          this.$store.dispatch('users/fetch', {})
        })
        .catch(error => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: error.response.data.message,
            status: 'danger',
          })
        })
      return doAnonymizeUser
    },
    reactivate(id) {
      const { remainingLicenses } = this.licenses
      if (remainingLicenses > 0) {
        this.$store
          .dispatch('users/reactivate', id)
          .then(() => {
            this.$root.$emit('app:message:show', {
              priority: HIGH,
              text: this.resources.general.message.reactivateSuccessfully,
              status: 'success',
            })
            this.$refs.licenseSummary.refreshLicenses()
          })
          .catch(() => {
            /* istanbul ignore next */
            this.$root.$emit('app:message:show', {
              priority: HIGH,
              text: this.resources.general.message.reactivateError,
              status: 'danger',
            })
          })
      } else {
        this.$refs.exceedLicense.show()
      }
    },
    removeFakeEmail,
    doCancelInvitation() {
      let doCancelInvitationUser = this.$store.dispatch(
        'users/cancelInvite',
        this.selectedUser
      )
      doCancelInvitationUser
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.general.message.cancelInvitedSuccessfully,
            status: 'success',
          })
          this.$refs.licenseSummary.refreshLicenses()
        })
        .catch(error => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: error.response.data.message,
            status: 'danger',
          })
        })
      return doCancelInvitationUser
    },
    resendInvite(email) {
      this.$store
        .dispatch('users/resendInvite', { email, jwt: localStorage.jwt })
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.general.message.invitedSuccessfully,
            status: 'success',
          })
        })
        .catch(error => {
          /* istanbul ignore next */
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: error.response.data.message,
            status: 'danger',
          })
        })
    },
    isNotMyUser(actualUserId) {
      return actualUserId !== this.user.id
    },
    inviteUsers() {
      const { remainingLicenses } = this.licenses

      if (remainingLicenses > 0) {
        this.$router.push({ name: 'dashboard.users.send.invite' })
      } else {
        this.$refs.exceedLicense.show()
      }
    },
    undoInactivate(id) {
      this.$store
        .dispatch('users/undoInactivate', id)
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.general.message.reactivateSuccessfully,
            status: 'success',
          })
          this.$refs.licenseSummary.refreshLicenses()
        })
        .catch(() => {
          /* istanbul ignore next */
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.general.message.reactivateError,
            status: 'danger',
          })
        })
    },
    doInactivate() {
      let doInactivatePromise = this.$store.dispatch(
        'users/inactivate',
        this.selectedUser
      )
      doInactivatePromise
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.labels.inactivatedSuccess,
            status: 'success',
          })
        })
        .catch(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.general.errors.response500,
            status: 'danger',
          })
        })
      return doInactivatePromise
    },
    doDisconfirmation() {
      let doDisconfirmationPromise = this.$store.dispatch(
        'users/disconfirm',
        this.selectedUser
      )
      doDisconfirmationPromise
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.labels.disconfirmSuccess,
            status: 'success',
          })
        })
        .catch(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.general.errors.response500,
            status: 'danger',
          })
        })
      return doDisconfirmationPromise
    },
    doDelete() {
      let doDeletePromise = this.$store.dispatch(
        'users/delete',
        this.selectedUser
      )
      doDeletePromise
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.labels.removedSuccess,
            status: 'success',
          })
          this.$refs.licenseSummary.refreshLicenses()
        })
        .catch(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.resources.general.errors.response500,
            status: 'danger',
          })
        })
      return doDeletePromise
    },
    applyFilters() {
      const status =
        this.actualStatus === 'undefined' ? undefined : this.actualStatus

      let filters = { status }

      filters.name = this.filterText

      if (this.filterTypeRole !== 'null') {
        filters.role = this.filterTypeRole
      }

      this.offset = 0
      this.page = 1

      this.$store.dispatch('users/fetch', filters)

      this.hasFilter =
        (this.filterTypeRole && this.filterTypeRole !== 'null') ||
        (this.filterText && this.filterText !== 'null') ||
        status

      this.toggleFilterPopover = false
    },
    clearFilters() {
      this.hasFilter = false
      this.filterText = undefined
      this.filterTypeRole = 'null'
      this.offset = 0
      this.page = 1
      this.actualStatus = 'undefined'
    },
    clearAndCloseFilters() {
      this.clearFilters()
      this.$store.dispatch('users/fetch', {})
      this.toggleFilterPopover = !this.toggleFilterPopover
    },
    confirmDelete(id) {
      this.selectedUser = id
      this.$refs.confirmDelete.show()
    },
    confirmInactivate(id) {
      this.selectedUser = id
      this.$refs.confirmInactivate.show()
    },
    confirmCancelInvitation(id) {
      this.selectedUser = id
      this.$refs.confirmCancelInvitation.show()
    },
    confirmDisconfirmation(id) {
      this.selectedUser = id
      this.$refs.confirmDisconfirmation.show()
    },
    edit(selectedUser) {
      this.$store.commit('users/selectUser', {
        ...selectedUser,
        email: removeFakeEmail(selectedUser.email),
      })
      this.$router.push({ name: 'dashboard.users.edit' })
    },
    exportFile() {
      const env = window.env || process.env
      window.open(
        `${env.VUE_APP_API_HOST}/${env.VUE_APP_API_NAMESPACE}/users/export?token=${localStorage.jwt}`,
        '_blank'
      )
    },
    handleExportClick() {
      this.$can('use', 'users.export')
        ? this.exportFile()
        : this.showModalEnterprise()
    },
    showModalEnterprise() {
      this.$refs['component-modal-plans'].show()
    },
    linkGen(pageNum) {
      return pageNum === 1 ? '?' : `?page=${pageNum}`
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

/deep/ select.input-white {
  background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSJub25lIiBkPSJNMCAwaDI0djI0SDBWMHoiLz48cGF0aCBkPSJNNy40MSA4LjU5TDEyIDEzLjE3bDQuNTktNC41OEwxOCAxMGwtNiA2LTYtNiAxLjQxLTEuNDF6Ii8+PC9zdmc+')
    no-repeat right 0.75rem center !important;
}

.user-status {
  font-weight: 500;
  font-size: 14px;

  .status {
    &.invited {
      color: map-get($theme-colors, 'orange');
    }

    &.confirmed {
      color: map-get($theme-colors, 'green-dark');
    }

    &.deactivated {
      color: map-get($theme-colors, 'gray02');
    }

    &.inactive {
      color: map-get($theme-colors, 'gray03');
    }
  }
}

.loading {
  margin: 0 auto;
  text-align: center;
}

button {
  &.fix-line-height {
    line-height: 0.95;
  }
  .material-icons {
    vertical-align: baseline;
  }
  span {
    vertical-align: text-bottom;
  }
}

.without-padding-bottom {
  margin-bottom: 20px;
}

.users-pagination {
  margin-bottom: 80px;
}

.card-header {
  padding-right: 15px;
}

.cancel-link {
  font-size: 14px;
}

.empty-state-box {
  overflow-y: hidden;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - 350px);
  text-align: center;
  width: 100%;

  video {
    text-align: center;
    min-height: 130px;
    max-height: 500px;
    max-width: 100%;
  }

  label {
    color: map-get($theme-colors, 'gray03');
    font-size: xx-large;
    @media (max-width: 991.98px) {
      font-size: large;
    }
  }
}

.header-text {
  font-size: 12px;
}

table-striped tbody tr:nth-of-type(odd) {
  background-color: #f6f7fd !important;
}

.flexbox {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-font-size {
  font-size: 14px;
}

.padding-row {
  padding-left: 12px;
}

@media (max-width: 991.98px) {
  #btn-invite-page {
    position: fixed;
    bottom: 50px;
    z-index: 1;
    right: 30px;
    width: 48px;
    height: 48px !important;
    margin: 0px !important;
    padding: 0px !important;
    font-size: large;
    box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.1);
  }

  #users-filter-btn {
    @media (max-width: 991.98px) {
      position: fixed;
      bottom: 110px !important;
      z-index: 1;
      right: 30px;
      width: 50px;
      margin: 0px;
      padding: 0px;
      box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.1);
      display: block !important;
    }
  }
}

.icon-profile {
  font-size: 39px;
  color: map-get($theme-colors, 'primary');
  width: 39px;
}

.ellipsis-text {
  white-space: nowrap;
  overflow: hidden !important;
  text-overflow: ellipsis;

  &.min-width {
    width: 10px;
  }
}

.full-table-wraper {
  margin-left: -20px;
  margin-right: -20px;
}

.apply-form-group-btn {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  height: 50px !important;
}

.name-form-group {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.fix-font-size {
  font-size: 1rem;
}

.status-tip,
.search-filters {
  @media (max-width: 991.98px) {
    display: none;
  }
}

.company-name {
  font-size: 1.25em;
  color: map-get($theme-colors, 'primary');
}

#filter-apply {
  font-size: 1.5rem;
  i {
    margin-top: 6px;
  }
}

.user-actions {
  justify-content: space-around !important;
  font-size: 1.4em;
  margin-top: 12px;
}

.modal-alert {
  color: map-get($theme-colors, 'gray02') !important;
  background-color: map-get($theme-colors, 'blue-light') !important;
  font-size: 0.8em !important;
  position: relative !important;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem !important;
}

.modal-secondary {
  color: map-get($theme-colors, 'white') !important;
  background-color: map-get($theme-colors, 'secondary') !important;
  font-size: 0.8em !important;
  position: relative !important;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem !important;

  .material-icons {
    margin-right: 10px;
    line-height: inherit !important;
  }
}

.label-deactivated {
  color: map-get($theme-colors, 'gray02') !important;
  .fullname {
    text-decoration: line-through !important;
  }
  i {
    color: map-get($theme-colors, 'gray02') !important;
  }
}

.label-inactive {
  color: map-get($theme-colors, 'gray03') !important;
  i {
    color: map-get($theme-colors, 'gray03') !important;
  }
}
</style>
